import { useQuery } from '@tanstack/react-query';
// api
import { dashboardApi } from '@/domains/traffic-monitoring/apis/dashboard';
// constant
import { DASHBOARD_CONSTANTS } from '@/domains/traffic-monitoring/constants/dashboard.constants';
import type { TrafficEventListTodayItem } from '../types/dashboard.types';

const ONE_HOUR_MS = 60 * 60 * 1000;

// Endpoint's own per-page ceiling (PSC_PC0203001_BE_007).
const PAGE_SIZE = 200;

// Endpoint's own `page * size <= 10000` ceiling - a hard stop so a freak
// event storm can't turn this into an unbounded fetch loop.
const MAX_FETCH = 10000;

// PSC_PC0203001_BE_007 - same endpoint as the day-scoped event list, but with
// its own rolling `start_time` recomputed on every fetch (not read from the
// date picker) so the window stays "now minus 1h" instead of growing as time
// passes. Fetches every page up to `total_count` so a busy hour's markers
// aren't missing CCTVs whose events happened to sort past the first page.
export const useRecentTrafficEvents = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['trafficEventListRecent'],
    queryFn: async () => {
      const startTime = new Date(Date.now() - ONE_HOUR_MS).toISOString();

      let page = 1;
      let events: TrafficEventListTodayItem[] = [];
      let hasMore = true;

      while (hasMore) {
        const response = await dashboardApi.getTrafficEventListToday({
          start_time: startTime,
          page,
          size: PAGE_SIZE,
        });
        const pageEvents = response?.data?.events ?? [];
        events = events.concat(pageEvents);

        const total = response?.data?.total_count ?? 0;
        hasMore =
          pageEvents.length > 0 && events.length < total && (page + 1) * PAGE_SIZE <= MAX_FETCH;
        page += 1;
      }
      return events;
    },
    staleTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.STALE_TIME_MS,
    gcTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.CACHE_TIME_MS,
    refetchInterval: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.REFETCH_INTERVAL_MS,
    placeholderData: (prev) => prev,
  });

  return { events: data ?? [], isLoading, isError };
};
